import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Alert,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import React from "react";
import { useCurrencyFormatter } from "../../hooks/useCurrencyFormatter";
import { ModeInsightsProps } from "../../types/insights";
import { yearsAndMonths } from "../../utils";
import ModeInsightsCard from "./ModeInsightsCard";
import StarIcon from "./StarIcon";

export default function PaymentFlexibilityInsights({
  model,
}: ModeInsightsProps) {
  const c = useCurrencyFormatter();
  const nSimulations = model.results.length;

  // Get common values from first simulation (all simulations share these)
  const firstSim = model.results.simulations[0];
  const principal = firstSim.principal;
  const interestRate = firstSim.mortgage.interestRate;

  // Find min/max term lengths
  const maxTermMonths = Math.max(
    ...model.results.simulations.map((sim) => sim.mortgage.termMonths),
  );
  const minTermMonths = Math.min(
    ...model.results.simulations.map((sim) => sim.mortgage.termMonths),
  );

  // Find the shortest term simulation and use its monthly payment as the budget
  // This is the correct approach since the shortest term dictates the monthly budget
  const shortestTermSim = model.results.simulations.reduce((shortest, sim) => {
    return sim.mortgage.termMonths < shortest.mortgage.termMonths
      ? sim
      : shortest;
  }, model.results.simulations[0]);
  const monthlyBudget = shortestTermSim.mortgage.desiredMonthlyPayment;

  // Find the longest term simulation to get ACR settings
  // (shortest term has ACRs disabled, so we read from longest which has ACRs enabled)
  const longestTermSim = model.results.simulations.reduce((longest, sim) => {
    return sim.mortgage.termMonths > longest.mortgage.termMonths
      ? sim
      : longest;
  }, model.results.simulations[0]);

  // Check if savings interest is configured (from longest term which has ACRs)
  const hasSavingsInterest =
    longestTermSim.savingsInterestRate !== undefined &&
    longestTermSim.savingsInterestRate > 0;
  const savingsInterestRate = longestTermSim.savingsInterestRate ?? 0;

  // Find cheapest simulation
  const cheapestSimulation = model.results.simulations.reduce(
    (cheapest, sim) => {
      return sim.totalCostOfMortgage < cheapest.totalCostOfMortgage
        ? sim
        : cheapest;
    },
    model.results.simulations[0],
  );

  // Calculate metrics for each simulation
  const simulationsWithMetrics = model.results.simulations.map((sim) => {
    const termMonths = sim.mortgage.termMonths;
    const monthlyPayment = sim.mortgage.desiredMonthlyPayment;
    const monthlyContribution = monthlyBudget - monthlyPayment;
    const totalAcr = sim.mortgage.ledger.entries.reduce(
      (sum, entry) => sum + entry.additionalCapitalRepayments,
      0,
    );
    const actualDuration = sim.numberOfMonths;
    const interestEarned = sim.totalInterestEarned;
    const savingsPercentage =
      sim.totalInterestPaid > 0
        ? ((interestEarned / sim.totalInterestPaid) * 100).toFixed(1)
        : "0";

    return {
      sim,
      termMonths,
      monthlyPayment,
      monthlyContribution,
      totalAcr,
      actualDuration,
      interestEarned,
      savingsPercentage,
    };
  });

  // Sort by term length (shortest first - least flexible)
  const sortedByTerm = [...simulationsWithMetrics].sort(
    (a, b) => a.termMonths - b.termMonths,
  );

  const shortestTerm = sortedByTerm[0]; // Least flexible (highest payment)
  const longestTerm = sortedByTerm[sortedByTerm.length - 1]; // Most flexible (lowest payment)

  // Calculate flexibility cost
  const flexibilityCost =
    longestTerm.sim.totalCostOfMortgage - shortestTerm.sim.totalCostOfMortgage;
  const monthlyPaymentDifference =
    shortestTerm.monthlyPayment - longestTerm.monthlyPayment;
  const durationDifference =
    longestTerm.actualDuration - shortestTerm.actualDuration;

  // Total interest earned across all simulations (for reference)
  const totalInterestEarnedShortestTerm = shortestTerm.interestEarned;
  const totalInterestEarnedLongestTerm = longestTerm.interestEarned;

  return (
    <ModeInsightsCard>
      {/* Header showing common loan details */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          {c(monthlyBudget, true)}{" "}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ display: "inline" }}
          >
            /month budget (based on {yearsAndMonths(minTermMonths)} term)
          </Typography>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {c(principal, true)} • {interestRate}%
        </Typography>
      </Box>

      <Typography variant="body2" gutterBottom>
        <strong>{nSimulations}</strong> simulations were compared with term
        lengths ranging from <strong>{yearsAndMonths(minTermMonths)}</strong> to{" "}
        <strong>{yearsAndMonths(maxTermMonths)}</strong>.
      </Typography>

      <Typography variant="body2" gutterBottom>
        The most cost-effective option was a{" "}
        <strong>
          {yearsAndMonths(cheapestSimulation.mortgage.termMonths)}
        </strong>{" "}
        term with a total cost of{" "}
        <strong>{c(cheapestSimulation.totalCostOfMortgage, true)}</strong>.
      </Typography>

      {/* Key insight about flexibility cost */}
      <Box
        sx={{
          mt: 2,
          mb: 2,
          p: 2,
          bgcolor: "warning.lighter",
          borderRadius: 1,
          border: "1px solid",
          borderColor: "warning.light",
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
          The Cost of Flexibility
        </Typography>
        <Typography variant="body2" gutterBottom>
          Choosing the longer{" "}
          <strong>{yearsAndMonths(longestTerm.termMonths)}</strong> term instead
          of the shorter{" "}
          <strong>{yearsAndMonths(shortestTerm.termMonths)}</strong> term
          reduces the committed monthly payment by{" "}
          <strong>{c(monthlyPaymentDifference, true)}/month</strong>.
        </Typography>
        <Typography variant="body2" gutterBottom>
          This flexibility costs an additional{" "}
          <strong>{c(flexibilityCost, true)}</strong> over the life of the
          mortgage
          {durationDifference > 0 && (
            <>
              {" "}
              and takes <strong>
                {yearsAndMonths(durationDifference)}
              </strong>{" "}
              longer to pay off
            </>
          )}
          .
        </Typography>
      </Box>

      {/* Explanation of the ACR strategy */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 2, mb: 1, fontStyle: "italic" }}
      >
        <strong>How does this work?</strong>
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        The monthly budget is set by the payment required for the shortest term
        ({yearsAndMonths(shortestTerm.termMonths)}). For longer terms, the
        required payment is lower, so the difference goes into a reserve
        account—providing a safety buffer if income is reduced or unexpected
        expenses arise.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        When conditions allow (typically once per year during a fixed deal, and
        unlimited between deals), funds from this reserve are used for
        Additional Capital Repayments (ACRs). This strategy aims to pay off the
        mortgage in a similar timeframe to the shorter term, while maintaining
        the flexibility of lower committed payments.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The "cost of flexibility" shown above represents the price paid for this
        safety net—primarily due to interest accruing on the higher balance for
        longer before ACRs are made, and potential lender limits on overpayment
        amounts.
      </Typography>

      {!hasSavingsInterest ? (
        <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
            Reserve Funds Are Not Earning Interest
          </Typography>
          <Typography variant="body2">
            The simulations do not include interest earned on the reserve
            account. In practice, the surplus funds waiting to be used for ACRs
            could be held in a high-interest, easy-access savings account. This
            would reduce the effective cost of flexibility by earning interest
            on the reserve balance each month. Consider re-running this
            simulation with savings interest enabled to see the potential
            benefit.
          </Typography>
        </Alert>
      ) : (
        <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
            Reserve Account Interest: {savingsInterestRate}%
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The reserve account is configured to earn{" "}
            <strong>{savingsInterestRate}%</strong> interest. This helps offset
            some of the cost of flexibility by earning returns on funds waiting
            to be used for ACRs.
          </Typography>
          {totalInterestEarnedLongestTerm > 0 && (
            <Typography variant="body2">
              The most flexible option ({yearsAndMonths(longestTerm.termMonths)}{" "}
              term) earns{" "}
              <strong>{c(totalInterestEarnedLongestTerm, true)}</strong> in
              reserve account interest, offsetting{" "}
              <strong>{longestTerm.savingsPercentage}%</strong> of the mortgage
              interest paid. This reduces the true cost of flexibility from{" "}
              <strong>
                {c(
                  flexibilityCost +
                    totalInterestEarnedLongestTerm -
                    totalInterestEarnedShortestTerm,
                  true,
                )}
              </strong>{" "}
              to <strong>{c(flexibilityCost, true)}</strong>.
            </Typography>
          )}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Committed Term</strong>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="The monthly payment committed to the lender">
                  <strong>Monthly Payment</strong>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Amount added to reserve each month (budget minus payment)">
                  <strong>Monthly Reserve</strong>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Actual time to pay off the mortgage with ACRs">
                  <strong>Actual Duration</strong>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <strong>Total Cost</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Total Interest</strong>
              </TableCell>
              {hasSavingsInterest && (
                <TableCell align="right">
                  <Tooltip title="Interest earned on reserve account funds">
                    <strong>Interest Earned</strong>
                  </Tooltip>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedByTerm.map(
              ({
                sim,
                termMonths,
                monthlyPayment,
                monthlyContribution,
                actualDuration,
                interestEarned,
              }) => (
                <TableRow
                  key={termMonths}
                  sx={{
                    backgroundColor:
                      sim === cheapestSimulation
                        ? "success.lighter"
                        : sim === longestTerm.sim
                          ? "warning.lighter"
                          : "inherit",
                  }}
                >
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {yearsAndMonths(termMonths)}
                    {sim === cheapestSimulation && <StarIcon />}
                  </TableCell>
                  <TableCell align="right">{c(monthlyPayment, true)}</TableCell>
                  <TableCell align="right">
                    {c(monthlyContribution, true)}
                  </TableCell>
                  <TableCell align="right">
                    {yearsAndMonths(actualDuration)}
                  </TableCell>
                  <TableCell align="right">
                    {c(sim.totalCostOfMortgage, true)}
                  </TableCell>
                  <TableCell align="right">
                    {c(sim.totalInterestPaid, true)}
                  </TableCell>
                  {hasSavingsInterest && (
                    <TableCell align="right">
                      {interestEarned > 0 ? c(interestEarned, true) : "-"}
                    </TableCell>
                  )}
                </TableRow>
              ),
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 2, fontSize: "0.75rem" }}
      >
        <strong>Note:</strong> The "Actual Duration" shows how long it takes to
        pay off the mortgage when ACRs are made. A longer committed term with
        regular ACRs may result in a similar actual duration to a shorter term,
        but with the security of lower required payments if needed. The cost
        difference represents the price of this financial flexibility.
      </Typography>
    </ModeInsightsCard>
  );
}
